import React, { useCallback, useState } from 'react';
import PropTypes from 'prop-types';
import { WeezHeader, Card, Button, Message, Form, Field, Input, ToggleCard, Icon, ToastContext, Modal } from '@weezevent/nacre';
import { useTranslation } from 'react-i18next';
import LinkedinLogo from './images/linkedin.png';
import FacebookLogo from './images/facebook.png';
import AnalyticsLogo from './images/analytics.png';
import GoogleLogo from './images/google-tag-manager.png';
import useAppContext from '../../../context/useAppContext';
import AnalyticsModel from '../../../models/analyticsModel';
import PassCultureEvent from '../../../models/passCultureEvent';
import LoadingCard from '../../../components/loadingCard';
import { requiredField, useForm } from '../../../utils/validators';
import useMixpanel from '../../../context/useMixpanel';
import '../../../styles/event/integration.scss';
import PassCultureToggleCard from './components/passCultureToggleCard';
import isPassCultureAllowed from './components/passCultureAllowedTypes';

function FormCodes({ options, isWithSection = true, formIntegration, setFormIntegration }) {
    return options.map(option => (
        <ToggleCard
            title={option.title}
            subtitle={option.subtitle}
            logoURL={option.logoURL}
            key={option.name}
            toggle={Boolean(formIntegration[option.name])}
            onToggle={option.onToggle}
        >
            {isWithSection && (
                <Field>
                    <label htmlFor={option.name}>{option.label}</label>
                    <Input
                        id={option.name}
                        value={formIntegration[option.name]}
                        onChange={(_, { value }) => setFormIntegration(option.name, value)}
                        placeholder={option.placeholder}
                    />
                </Field>
            )}
        </ToggleCard>
    ));
}

export default function Integration({ event, organization }) {
    const { t } = useTranslation();
    const toast = React.useContext(ToastContext);
    const mixpanel = useMixpanel();

    const [{ reloadEvent: reloadEventContext }] = useAppContext();

    const [isModalOpen, setIsModalOpen] = useState(false);
    const [loadingPassCulture, setLoadingPassCulture] = useState(false);
    const [createdPassCulture, setCreatedPassCulture] = useState(false);

    const [formPassCulture, , setFormPassCultureObj, formErrors, formValidate] = useForm(
        {
            is_passculture_activated: false,
            siret: '',
            venue: '',
            address: null,
            venues: [],
            enableDoubleBookings: false
        },
        {
            siret: requiredField,
            venue: requiredField,
            enableDoubleBookings: requiredField
        }
    );

    const [formIntegration, setFormIntegration, setFormIntegrationObj] = useForm({
        pass_culture: '',
        facebook_id_pixel: '',
        linkedin_event_id: '',
        google_tag_manager: '',
        code_analytics: '',
        agenda_partenaire: false
    });

    const passCultureAllowed = React.useMemo(() => isPassCultureAllowed(event.subtype), [event]);

    const [passCultureEvent, loadingEvent, syncPass] = PassCultureEvent.useApiModel(
        {
            organizationId: organization.id,
            eventId: event.id,
            launch: Boolean(!event.is_seated),
            onSuccess: result => {
                if (result?.response?.siret) {
                    PassCultureEvent.getVenues({
                        organizationId: organization.id,
                        eventId: event.id,
                        siret: result.response.siret
                    }).then(data => {
                        const selectedVenue = data?.response;
                        setFormPassCultureObj({
                            is_passculture_activated: result.response.id,
                            siret: result.response.siret || '',
                            address: result.response.address_id || null,
                            venue: selectedVenue?.id || '',
                            venues: [data?.response] || [],
                            enableDoubleBookings: result.response.is_double_booking_enabled || false
                        });
                    });
                }
            }
        },
        [organization, event]
    );

    const createEventProviderPassCulture = useCallback(() => {
        // create eventProvider
        const newPassCultureEvent = new PassCultureEvent({
            siret: formPassCulture.siret,
            venue_id: formPassCulture.venue,
            is_double_booking_enabled: formPassCulture.enableDoubleBookings,
            address_id: formPassCulture.address
        });
        // to prevent multiple create between request and response
        setLoadingPassCulture(true);
        setCreatedPassCulture(true);
        newPassCultureEvent
            .save({ organizationId: organization.pk, eventId: event.pk })
            .then(data => {
                toast.success(t('ticket.form.details.submit.success'));
                reloadEventContext(false);
            })
            .catch(data => {
                toast.error(t('ticket.event.integration.passculture.failure-toast'));
                setCreatedPassCulture(false);
            })
            .finally(() => {
                setLoadingPassCulture(false);
                syncPass();
            });
    }, [formPassCulture, syncPass, organization, event, reloadEventContext]);

    const [analytics, loading, sync] = AnalyticsModel.useApiModel({
        organizationId: organization.pk,
        eventId: event.pk,
        cache: false,
        allow_cache: false,
        onSuccess: data => {
            let formatedValues = {
                pass_culture: data.pass_culture,
                facebook_id_pixel: data.facebook_id_pixel,
                linkedin_event_id: data.linkedin_event_id,
                google_tag_manager: data.google_tag_manager,
                code_analytics: data.code_analytics,
                agenda_partenaire: Boolean(Number(data.agenda_partenaire))
            };
            setFormIntegrationObj({ ...data, ...formatedValues });
        }
    });

    const onSubmit = React.useCallback(
        (validatedModal = false) => {
            // passCulture create
            if (formPassCulture.is_passculture_activated) {
                if (!formValidate()) return;
                if (typeof validatedModal === 'boolean' && validatedModal) {
                    createEventProviderPassCulture();
                } else {
                    setIsModalOpen(true);
                    return;
                }
            }
            const newAnalytics = new AnalyticsModel({
                ...analytics,
                pass_culture: formIntegration['pass_culture'],
                facebook_id_pixel: formIntegration['facebook_id_pixel'],
                linkedin_event_id: formIntegration['linkedin_event_id'],
                google_tag_manager: formIntegration['google_tag_manager'],
                code_analytics: formIntegration['code_analytics'],
                agenda_partenaire: formIntegration['agenda_partenaire'] ? '1' : '0'
            });
            newAnalytics
                .save({
                    organizationId: organization.pk,
                    eventId: event.pk
                })
                .then(_ => {
                    const trackingData = {
                        'Page Title': 'INTEGRATION',
                        'Activate Analytics': Boolean(newAnalytics['code_analytics']),
                        'Activate Tag Manager': Boolean(newAnalytics['google_tag_manager']),
                        'Activate Facebook': Boolean(newAnalytics['facebook_id_pixel']),
                        'Activate Pass Culture': Boolean(newAnalytics['pass_culture'] || formPassCulture.is_passculture_activated),
                        'Activate Linkedin': Boolean(newAnalytics['linkedin_event_id']),
                        'Activate Agenda': Boolean(newAnalytics['agenda_partenaire'] === '1')
                    };
                    mixpanel.track('ACTION > SAVE INTEGRATION', trackingData);
                    toast.success(t(`ticket.event.integration.success-toast`));
                })
                .catch(err => {
                    console.error('Error', err);
                    toast.error(t(`ticket.event.integration.failure-toast`));
                })
                .finally(() => {
                    sync();
                });
        },
        [analytics, formIntegration, sync, mixpanel, formValidate, formPassCulture, passCultureEvent]
    );

    const options = [
        {
            name: 'code_analytics',
            placeholder: 'G-XXXXXXXXXX',
            logoURL: AnalyticsLogo,
            label: t('ticket.event.integration.analytics-label'),
            title: t('ticket.event.integration.analytics'),
            subtitle: t('ticket.event.integration.analytic-description'),
            onToggle: checked => !checked && setFormIntegration('code_analytics', '')
        },
        {
            name: 'google_tag_manager',
            placeholder: 'GTM-XXXXXXX',
            logoURL: GoogleLogo,
            label: t('ticket.event.integration.google-label'),
            title: t('ticket.event.integration.google'),
            subtitle: t('ticket.event.integration.google-description'),
            onToggle: checked => !checked && setFormIntegration('google_tag_manager', '')
        },
        {
            name: 'facebook_id_pixel',
            placeholder: '123456789012345',
            logoURL: FacebookLogo,
            label: t('ticket.event.integration.facebook-label'),
            title: t('ticket.event.integration.facebook'),
            subtitle: t('ticket.event.integration.facebook-description'),
            onToggle: checked => !checked && setFormIntegration('facebook_id_pixel', '')
        }
    ];

    return (
        <>
            <WeezHeader
                title={t('ticket.event.integration.title')}
                rightComponent={[
                    <Button
                        primary
                        icon={<Icon className="cta-save-icon" name="save" margin={false} />}
                        key="primary"
                        disabled={loading}
                        label={t('ticket.common.action.save')}
                        onClick={onSubmit}
                    />
                ]}
            >
                {t('ticket.event.integration.subtitle')}
            </WeezHeader>
            <Form>
                <Card.FormManager useSteps={false}>
                    <Card.FormStep
                        title={t('ticket.event.integration.codes')}
                        rightComponent={
                            <Message type="tips" title={t('notifications.tips_title')}>
                                {t('ticket.event.integration.tips')}
                            </Message>
                        }
                    >
                        <LoadingCard loading={loading}>
                            <FormCodes options={options} formIntegration={formIntegration} setFormIntegration={setFormIntegration} />
                        </LoadingCard>
                    </Card.FormStep>
                    {!event.is_seated && !event.use_sessions && passCultureAllowed ? (
                        <Card.FormStep title={t('ticket.event.integration.external')} rightComponent>
                            <LoadingCard loading={loading || loadingEvent || loadingPassCulture}>
                                <Modal
                                    open={isModalOpen}
                                    title={t('ticket.event.integration.passculture.modale-confirmation.title')}
                                    subtitle={t('ticket.event.integration.passculture.modale-confirmation.content')}
                                    onClose={() => setIsModalOpen(false)}
                                    submitButton={
                                        <Button
                                            primary
                                            onClick={() => {
                                                setIsModalOpen(false);
                                                onSubmit(true);
                                            }}
                                            label={t('ticket.common.action.validate')}
                                        />
                                    }
                                    cancelButton={<Button onClick={() => setIsModalOpen(false)} label={t('ticket.common.action.cancel')} />}
                                />
                                <PassCultureToggleCard
                                    disabled={loadingEvent || loadingPassCulture || createdPassCulture}
                                    organizationId={organization.id}
                                    eventId={event.id}
                                    eventSubType={event.subtype}
                                    eventProvider={passCultureEvent}
                                    form={formPassCulture}
                                    setForm={setFormPassCultureObj}
                                    formErrors={formErrors}
                                />
                            </LoadingCard>
                        </Card.FormStep>
                    ) : null}
                    <Card.FormStep title={t('ticket.event.integration.promote')} rightComponent>
                        <LoadingCard loading={loading}>
                            <FormCodes
                                options={[
                                    {
                                        name: 'linkedin_event_id',
                                        placeholder: t('ticket.event.integration.linkedin-placeholder'),
                                        logoURL: LinkedinLogo,
                                        label: t('ticket.event.integration.linkedin-label'),
                                        title: t('ticket.event.integration.linkedin'),
                                        subtitle: t('ticket.event.integration.linkedin-description'),
                                        onToggle: checked => !checked && setFormIntegration('linkedin_event_id', '')
                                    }
                                ]}
                                formIntegration={formIntegration}
                                setFormIntegration={setFormIntegration}
                            />
                        </LoadingCard>
                    </Card.FormStep>
                    <Card.FormStep title={t('ticket.event.integration.partners.title')} rightComponent>
                        <LoadingCard loading={loading}>
                            <FormCodes
                                options={[
                                    {
                                        name: 'agenda_partenaire',
                                        title: t('ticket.event.integration.partners.card.title'),
                                        subtitle: t('ticket.event.integration.partners.card.subtitle'),
                                        onToggle: checked => setFormIntegration('agenda_partenaire', checked)
                                    }
                                ]}
                                isWithSection={false}
                                formIntegration={formIntegration}
                                setFormIntegration={setFormIntegration}
                            />
                        </LoadingCard>
                    </Card.FormStep>
                </Card.FormManager>
            </Form>
        </>
    );
}

Integration.propTypes = {
    organization: PropTypes.object.isRequired,
    event: PropTypes.object.isRequired
};
